import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/base/SEOHead';
import { categories, articles, faqItems } from '@/mocks/articles';

function HeroSection() {
  return (
    <section className="relative h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://bodymindapp.github.io/api/search-image?query=Serene%20minimalist%20health%20and%20wellness%20concept%20with%20soft%20natural%20light%20warm%20cream%20tones%20peaceful%20abstract%20composition%20gentle%20gradients%20clean%20medical%20aesthetic&width=1600&height=900&seq=hero-main-bg&orientation=landscape"
          alt="健康认知"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
      </div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          手淫的科学认知
          <br />
          <span className="text-2xl md:text-4xl lg:text-5xl font-normal">与健康指南</span>
        </h1>
        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          基于医学与心理学的权威科普，帮助您正确认识手淫对身心健康的影响，
          破除迷信与羞耻感，建立理性健康的自我认知与行为管理策略。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/kexue-renzhi"
            className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            开始探索
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
          <Link
            to="/about"
            className="px-6 py-3 bg-white/15 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/25 transition-colors duration-200 whitespace-nowrap cursor-pointer border border-white/20"
          >
            关于我们
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles() {
  const featured = articles.slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: featured.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        url: `https://bodymindapp.github.io/article/${article.slug}`,
      },
    })),
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            精选文章
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            由专业团队撰写，基于循证医学证据的深度科普内容
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featured.slice(0, 2).map((article) => (
            <ArticleCard key={article.slug} article={article} variant="featured" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          {featured.slice(2, 4).map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryGrid() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            探索主题
          </h2>
          <p className="text-foreground-500 max-w-xl mx-auto">
            六大主题分类，系统化了解手淫相关的科学知识
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex items-start gap-4 p-5 rounded-lg bg-white hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  cat.color === 'primary'
                    ? 'bg-primary-100 text-primary-600'
                    : cat.color === 'accent'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors duration-200">
                  {cat.name}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {cat.description}
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-primary-600 mt-2 font-medium">
                  浏览文章
                  <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            常见问题
          </h2>
          <p className="text-foreground-500">
            您可能关心的问题，这里有科学的答案
          </p>
        </div>
        <div className="space-y-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className="border border-background-200 rounded-lg overflow-hidden"
            >
              <button
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-background-50 transition-colors duration-200 cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-medium text-foreground-800 pr-4 text-sm md:text-base">
                  {item.question}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="contact_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const formData = new FormData(form);
      const resp = await fetch('https://bodymindapp.github.io/api/form/d9u4k75ca23r0tr1aqf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请稍后重试');
    }
  };

  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-primary-100 flex items-center justify-center">
          <i className="ri-mail-send-line text-2xl text-primary-600"></i>
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
          订阅我们的通讯
        </h2>
        <p className="text-foreground-500 mb-8">
          获取最新的性健康科普文章，保持科学理性的健康观念
        </p>
        {status === 'success' ? (
          <div className="bg-primary-50 text-primary-700 px-5 py-4 rounded-lg text-sm">
            感谢订阅！我们会将最新文章发送到您的邮箱。
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入您的邮箱"
              required
              className="flex-1 px-4 py-3 rounded-lg border border-background-300 bg-white text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all duration-200"
            />
            <input
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              aria-hidden="true"
              className="form-website-alt-field"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              订阅
            </button>
          </form>
        )}
        {status === 'error' && (
          <p className="text-red-500 text-sm mt-3">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative py-20 md:py-24 px-4 md:px-6 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://bodymindapp.github.io/api/search-image?query=Warm%20minimalist%20wellness%20lifestyle%20background%20with%20soft%20natural%20light%20cream%20and%20beige%20tones%20peaceful%20atmosphere%20clean%20medical%20aesthetic&width=1600&height=600&seq=cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
      </div>
      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
          知识是消除恐惧的最好方式
        </h2>
        <p className="text-white/80 mb-8 text-base md:text-lg">
          用科学的态度理解自己的身体，用健康的心态面对自然的行为
        </p>
        <Link
          to="/category/xing-jiaoyu"
          className="inline-flex items-center px-6 py-3 bg-white text-foreground-900 font-medium rounded-lg hover:bg-white/90 transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          开始学习
          <i className="ri-arrow-right-line ml-2"></i>
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  const siteUrl = 'https://bodymindapp.github.io';

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '手淫的科学认知与健康指南',
    url: siteUrl,
    description: '基于医学与心理学的权威科普，帮助正确认识手淫对身心健康的影响，破除迷信与羞耻感，建立理性健康的自我认知',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '健康认知指南',
    url: siteUrl,
    description: '提供基于医学与心理学的权威性健康科普平台',
  };

  return (
    <>
      <SEOHead
        title="手淫的科学认知与健康指南 | 权威性健康教育平台"
        description="基于医学与心理学的权威科普，帮助正确认识手淫对身心健康的影响，破除迷信与羞耻感，建立理性健康的自我认知与行为管理策略。涵盖科学认知、频率与健康、心理影响、常见误区等全面内容。"
        keywords="手淫,手淫频率与健康,手淫的科学认知,手淫心理影响,手淫常见误区,成年人性健康教育,自慰行为管理"
        canonicalUrl={siteUrl}
        ogImage={`https://bodymindapp.github.io/api/search-image?query=Health%20education%20website%20with%20warm%20professional%20medical%20aesthetic%20clean%20minimalist%20design&width=1200&height=630&seq=og-home&orientation=landscape`}
      />

      <Navbar />
      <main>
        <HeroSection />
        <FeaturedArticles />
        <CategoryGrid />
        <FAQSection />
        <CTA />
        <NewsletterSection />
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
    </>
  );
}